import { type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLink } from "shared/components/common/base-link";
import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { article as articleData } from "shared/data";

import type { Props as GenericArticleProps } from "scmp-app/components/article/article-render/generic-article";
import { getArticleType } from "scmp-app/components/article/article-render/helpers";
import type { postMagazineArticleContent$key } from "scmp-app/queries/__generated__/postMagazineArticleContent.graphql";

import {
  BannerContainer,
  RightBanner,
  RightBannerText,
  StyledGenericArticle,
  StyledLogo,
  StyledPhotoEssayAndLongReadArticle,
} from "./styles";

export type Props = Omit<GenericArticleProps, "reference"> & {
  reference: postMagazineArticleContent$key;
};

export const PostMagazineArticle: FunctionComponent<Props> = ({ reference, ...props }) => {
  const article = useFragment(
    graphql`
      fragment postMagazineArticleContent on Content {
        ...helpersGetArticleTypeArticle
        ...genericArticleContent
      }
    `,
    reference,
  );

  const articleType = getArticleType(article);

  const renderExtendedHeader = useCallback(
    () => (
      <BaseLinkContextProvider
        customQueryParameters={{
          module: "banner",
        }}
      >
        <BannerContainer>
          <BaseLink pathname="/postmag">
            <StyledLogo />
          </BaseLink>
          <RightBanner>
            <RightBannerText>Life.</RightBannerText>
            <RightBannerText>Culture.</RightBannerText>
            <RightBannerText>Discovery.</RightBannerText>
          </RightBanner>
        </BannerContainer>
      </BaseLinkContextProvider>
    ),
    [],
  );

  switch (articleType) {
    case articleData.types.longRead.entityId:
    case articleData.types.photoEssay.entityId:
      return (
        <StyledPhotoEssayAndLongReadArticle
          {...props}
          extendedHeaderComponents={renderExtendedHeader()}
          reference={article}
        />
      );
    default:
      return (
        <StyledGenericArticle
          {...props}
          extendedHeaderComponents={renderExtendedHeader()}
          reference={article}
        />
      );
  }
};

PostMagazineArticle.displayName = "PostMagazineArticle";
